/* eslint-disable react/require-default-props */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import './ToolbarStyles.scss';
import { ToolbarGroup } from './ToolbarGroup';
import { ToolbarSelection } from './ToolbarSelection';
import { ToolbarDivider } from './ToolbarDivider';
import { ToolbarOverflow } from './ToolbarOverflow';
const OVERFLOW_BUTTON_WIDTH = 96;
const ITEM_GAP = 8;
const DIVIDER_WIDTH = 9; // 1px + 4px margin each side
function isToolbarGroup(child) {
    if (!React.isValidElement(child))
        return false;
    return child.type.displayName === 'ToolbarGroup' || child.type === ToolbarGroup;
}
function isToolbarSelection(child) {
    if (!React.isValidElement(child))
        return false;
    return child.type === ToolbarSelection || child.type.displayName === 'ToolbarSelection';
}
function isGroupEnd(child) {
    return child.props.align === 'end';
}
function isGroupPinned(child) {
    const props = child.props;
    return props.pinned === true || props.align === 'end';
}
function isSelectionVisible(child) {
    var _a;
    if (!isToolbarSelection(child))
        return true;
    return ((_a = child.props.count) !== null && _a !== void 0 ? _a : 0) > 0;
}
function groupChildArray(group) {
    return React.Children.toArray(group.props.children);
}
function itemKey(groupIndex, itemIndex) {
    return `${groupIndex}:${itemIndex}`;
}
/**
 * Toolbar Component
 *
 * Horizontal toolbar that renders grouped action buttons with a vertical divider
 * only when a selection count is visible. Overflow hides individual actions from
 * the right (same algorithm as OverflowActionsMenu) into a More menu.
 *
 * @version 1.2.0
 * @since 2026-03-24
 */
export function Toolbar({ children, className = '', moreActionsLabel }) {
    const containerRef = useRef(null);
    const groupRefs = useRef([]);
    const itemRefs = useRef({});
    const [overflowKeys, setOverflowKeys] = useState([]);
    const childArray = React.Children.toArray(children);
    const groupChildren = childArray.filter(isToolbarGroup);
    const nonGroupChildren = childArray.filter((c) => !isToolbarGroup(c));
    const hasVisibleSelection = nonGroupChildren.some(isSelectionVisible);
    useEffect(() => {
        groupRefs.current.length = groupChildren.length;
    }, [groupChildren.length]);
    const checkOverflow = useCallback(() => {
        if (!containerRef.current)
            return;
        const containerWidth = containerRef.current.clientWidth;
        if (containerWidth === 0)
            return;
        const groups = groupRefs.current.filter(Boolean);
        if (groups.length === 0)
            return;
        let pinnedWidth = 0;
        const unpinnedItems = [];
        groupChildren.forEach((group, groupIndex) => {
            const pinned = isGroupPinned(group);
            if (pinned) {
                const el = groups[groupIndex];
                if (el)
                    pinnedWidth += el.offsetWidth;
                return;
            }
            groupChildArray(group).forEach((_, itemIndex) => {
                var _a;
                const el = itemRefs.current[itemKey(groupIndex, itemIndex)];
                const width = (_a = el === null || el === void 0 ? void 0 : el.offsetWidth) !== null && _a !== void 0 ? _a : 0;
                unpinnedItems.push({ key: itemKey(groupIndex, itemIndex), width });
            });
        });
        if (unpinnedItems.some((item) => item.width === 0))
            return;
        const moreAndGap = OVERFLOW_BUTTON_WIDTH + ITEM_GAP;
        const selectionEl = containerRef.current.querySelector('.qmb-ui-toolbar__selection');
        const selectionWidth = selectionEl instanceof HTMLElement ? selectionEl.offsetWidth : 0;
        const dividerWidth = hasVisibleSelection && selectionWidth > 0 ? DIVIDER_WIDTH : 0;
        let used = pinnedWidth + selectionWidth + dividerWidth;
        unpinnedItems.forEach((item, index) => {
            used += item.width + (index > 0 ? ITEM_GAP : 0);
        });
        if (used <= containerWidth - 4) {
            setOverflowKeys((prev) => (prev.length === 0 ? prev : []));
            return;
        }
        const available = containerWidth - pinnedWidth - selectionWidth - dividerWidth - moreAndGap;
        let currentWidth = 0;
        let lastFitIndex = -1;
        for (let i = 0; i < unpinnedItems.length; i += 1) {
            const additional = currentWidth === 0 ? unpinnedItems[i].width : ITEM_GAP + unpinnedItems[i].width;
            if (currentWidth + additional <= available) {
                currentWidth += additional;
                lastFitIndex = i;
            }
            else {
                break;
            }
        }
        const nextKeys = unpinnedItems.slice(lastFitIndex + 1).map((item) => item.key);
        setOverflowKeys((prev) => {
            if (prev.length === nextKeys.length && prev.every((key, index) => key === nextKeys[index])) {
                return prev;
            }
            return nextKeys;
        });
    }, [groupChildren, hasVisibleSelection]);
    useEffect(() => {
        const timeoutIds = [];
        const rafId = requestAnimationFrame(() => {
            timeoutIds.push(setTimeout(checkOverflow, 0));
        });
        timeoutIds.push(setTimeout(checkOverflow, 50));
        timeoutIds.push(setTimeout(checkOverflow, 150));
        timeoutIds.push(setTimeout(checkOverflow, 300));
        window.addEventListener('resize', checkOverflow);
        const observer = typeof ResizeObserver !== 'undefined' && containerRef.current ? new ResizeObserver(checkOverflow) : null;
        if (observer && containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => {
            window.removeEventListener('resize', checkOverflow);
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            cancelAnimationFrame(rafId);
            timeoutIds.forEach((id) => clearTimeout(id));
        };
    }, [checkOverflow, children]);
    const overflowChildren = [];
    const overflowKeySet = new Set(overflowKeys);
    groupChildren.forEach((group, groupIndex) => {
        if (isGroupPinned(group))
            return;
        groupChildArray(group).forEach((child, itemIndex) => {
            if (overflowKeySet.has(itemKey(groupIndex, itemIndex))) {
                overflowChildren.push(child);
            }
        });
    });
    const combinedClass = ['qmb-ui-toolbar', className].filter(Boolean).join(' ');
    const firstStartGroupIndex = groupChildren.findIndex((group) => !isGroupEnd(group));
    const startGroupIndexes = groupChildren
        .map((group, groupIndex) => (isGroupEnd(group) ? -1 : groupIndex))
        .filter((groupIndex) => groupIndex >= 0);
    const endGroupIndexes = groupChildren
        .map((group, groupIndex) => (isGroupEnd(group) ? groupIndex : -1))
        .filter((groupIndex) => groupIndex >= 0);
    const renderGroup = (groupIndex) => {
        const group = groupChildren[groupIndex];
        const isPinned = isGroupPinned(group);
        const isEnd = isGroupEnd(group);
        const items = groupChildArray(group);
        const groupHasVisibleItem = isPinned || items.some((_, itemIndex) => !overflowKeySet.has(itemKey(groupIndex, itemIndex)));
        return (React.createElement(React.Fragment, { key: groupIndex },
            hasVisibleSelection && groupIndex === firstStartGroupIndex && groupHasVisibleItem && React.createElement(ToolbarDivider, null),
            React.createElement("div", { className: isEnd ? 'qmb-ui-toolbar__end' : undefined, ref: (el) => {
                    groupRefs.current[groupIndex] = el;
                }, style: !isPinned && !groupHasVisibleItem
                    ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }
                    : undefined }, isPinned ? (group) : (React.createElement("div", { className: "qmb-ui-toolbar__group" }, items.map((child, itemIndex) => {
                const key = itemKey(groupIndex, itemIndex);
                const isOverflowed = overflowKeySet.has(key);
                return (React.createElement("div", { key: key, ref: (el) => {
                        itemRefs.current[key] = el;
                    }, className: "qmb-ui-toolbar__item", style: isOverflowed ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' } : undefined }, child));
            }))))));
    };
    return (React.createElement("div", { className: combinedClass, ref: containerRef },
        nonGroupChildren,
        startGroupIndexes.map(renderGroup),
        overflowChildren.length > 0 && (React.createElement(ToolbarOverflow, { overflowChildren: overflowChildren, moreActionsLabel: moreActionsLabel })),
        endGroupIndexes.map(renderGroup)));
}
Toolbar.Group = ToolbarGroup;
Toolbar.Selection = ToolbarSelection;
